/**
 * Live-preview tag-value source for the computed-tags editor.
 *
 * Phase 0 maintains a module-level "latest value per tag" map that
 * drifts each second around the fixture seeds. The editor's preview
 * pane subscribes via `useLatestTagValues()`. Phase 1 replaces this
 * source with a WebSocket / polled `/v1/iot/tags/{id}/latest` stream.
 *
 * Module-level state + subscribers (no React Context) so the tick
 * doesn't churn the render tree — only components that subscribe
 * re-render.
 */

import { useEffect, useState } from "react";
import { compile, buildScope } from "./engine";
import { FX_INPUT_SEED_VALUES } from "./__fixtures";
import type { ComputedTagDef } from "./types";

interface LiveState {
  values: Map<string, number>;
  tick: number;
}

const state: LiveState = {
  values: new Map(),
  tick: 0,
};

// Seed from fixtures on module load.
for (const [tagId, seed] of Object.entries(FX_INPUT_SEED_VALUES)) {
  state.values.set(tagId, seed.value);
}

type Listener = () => void;
const listeners = new Set<Listener>();
let timer: ReturnType<typeof setInterval> | null = null;
let refCount = 0;

function notify() {
  for (const l of listeners) l();
}

/** Tick once — random-walk each tag inside its drift envelope. */
function tickOnce() {
  for (const [tagId, seed] of Object.entries(FX_INPUT_SEED_VALUES)) {
    if (seed.drift === 0) continue;
    const current = state.values.get(tagId) ?? seed.value;
    const step = (Math.random() - 0.5) * 2 * seed.drift;
    // Light gravity pull back toward seed so values don't wander off.
    const pull = (seed.value - current) * 0.05;
    state.values.set(tagId, current + step + pull);
  }
  state.tick += 1;
  notify();
}

/** Start / stop the tick lazily based on subscriber count. */
function ensureRunning() {
  if (timer !== null) return;
  timer = setInterval(tickOnce, 1000);
}
function stopIfIdle() {
  if (refCount === 0 && timer !== null) {
    clearInterval(timer);
    timer = null;
  }
}

/**
 * Subscribe to the latest-tag-values map. Returns a `Map<tagId, value>`
 * snapshot that updates every second. Optionally pass a list of tag ids
 * to filter — useful in the editor preview pane.
 */
export function useLatestTagValues(tagIds?: string[]): {
  values: Record<string, number>;
  tick: number;
} {
  const [snapshot, setSnapshot] = useState(() => readSnapshot(tagIds));

  useEffect(() => {
    refCount += 1;
    ensureRunning();
    const listener: Listener = () => setSnapshot(readSnapshot(tagIds));
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
      refCount -= 1;
      stopIfIdle();
    };
  }, [tagIds?.join("|")]);

  return snapshot;
}

function readSnapshot(tagIds?: string[]): {
  values: Record<string, number>;
  tick: number;
} {
  const out: Record<string, number> = {};
  if (tagIds && tagIds.length > 0) {
    for (const id of tagIds) {
      const v = state.values.get(id);
      if (typeof v === "number") out[id] = v;
    }
  } else {
    for (const [id, v] of state.values.entries()) out[id] = v;
  }
  return { values: out, tick: state.tick };
}

/**
 * Compute the live output of a computed-tag def against the current
 * latest values. Returns `null` on parse error or NaN.
 */
export function useLiveOutput(def: ComputedTagDef | null): {
  output: number | null;
  inputs: Record<string, number>;
  error: string | null;
  tick: number;
} {
  const inputIds = def?.inputs.map((i) => i.tagId) ?? [];
  const { values, tick } = useLatestTagValues(inputIds);

  if (!def) return { output: null, inputs: {}, error: null, tick };

  try {
    const compiled = compile(def.expression);
    const scope = buildScope(def, values);
    const result = compiled.evaluate(scope);
    if (typeof result !== "number" || !Number.isFinite(result)) {
      return { output: null, inputs: values, error: "Result is not finite", tick };
    }
    return { output: result, inputs: values, error: null, tick };
  } catch (err) {
    return {
      output: null,
      inputs: values,
      error: err instanceof Error ? err.message : String(err),
      tick,
    };
  }
}

/**
 * Write-only override — used by integration tests to set deterministic
 * values for snapshot purposes. Not used in the production demo.
 */
export function _setLatestValueForTesting(tagId: string, value: number) {
  state.values.set(tagId, value);
  notify();
}
